/**
 * Paddle.js singleton
 *
 * `initializePaddle()` must only run once per page — this module-level cache
 * ensures every caller of `usePaddle()` shares the same instance/promise.
 */
import { initializePaddle, type Paddle } from '@paddle/paddle-js'
import { computed, ref, watch } from 'vue'
import { logPurchase } from '@/composables/useAnalytics'
import { paddleConfig } from '@/config/paddle'
import { usePlanStore } from '@/stores/plan'
import { useUserStore } from '@/stores/user'

let paddleInstance: Paddle | undefined
let initPromise: Promise<Paddle | undefined> | null = null
let appliedCustomerId: string | undefined

// `paddleCustomerId` is the Paddle customer ID (ctm_...) used by Paddle Retain; omitted until the user has one.
function initPaddle (paddleCustomerId?: string): Promise<Paddle | undefined> {
    if (!initPromise) {
        initPromise = initializePaddle({
            ...paddleConfig,
            ...(paddleCustomerId && { pwCustomer: { id: paddleCustomerId } }),
            eventCallback: event => {
                if (event.name === 'checkout.completed' && event.data?.status === 'completed') {
                    logPurchase(event.data)
                }
            },
        }).then(paddle => {
            paddleInstance = paddle
            return paddle
        })
    }

    return initPromise
}

export function usePaddle () {
    const planStore = usePlanStore()
    const userStore = useUserStore()
    const paddle = ref<Paddle | undefined>(paddleInstance)
    const loading = ref(!paddleInstance)

    // Wait for the signed-in user's document so pwCustomer can be passed to Paddle.Initialize()
    const ready = computed(() => !!userStore.firebaseUid && !planStore.userDocPending)
    const customerId = computed(() => planStore.userDoc?.paddleCustomerId)

    watch([ready, customerId], ([isReady, id]) => {
        if (!isReady) {
            return
        }

        if (paddleInstance) {
            // The customer ID can appear after init (e.g. right after the first checkout)
            if (id && id !== appliedCustomerId) {
                appliedCustomerId = id
                paddleInstance.Update({ pwCustomer: { id } })
            }
            return
        }

        appliedCustomerId = id
        initPaddle(id).then(instance => {
            paddle.value = instance
            loading.value = false
        })
    }, { immediate: true })

    return { paddle, loading }
}
